import React from 'react';
import { X, Target } from 'lucide-react';
import { VARIABLE_BY_ID } from '../../data/formulas';
import { InlineLatex } from './LatexRenderer';

interface FormulaSelectedBadgesProps {
  selectedKnowns: string[];
  selectedTarget: string;
  onRemoveKnown: (id: string) => void;
  onClearTarget: () => void;
  onClearAll: () => void;
}

export const FormulaSelectedBadges: React.FC<FormulaSelectedBadgesProps> = ({
  selectedKnowns,
  selectedTarget,
  onRemoveKnown,
  onClearTarget,
  onClearAll,
}) => {
  return (
    <div className="space-y-4">
      {/* Step 1: Known Variables Chips */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">
              1
            </span>
            <span className="font-serif text-xs font-bold text-zinc-900 dark:text-zinc-100">
              Dados Conhecidos ({selectedKnowns.length})
            </span>
          </div>
          {selectedKnowns.length > 0 && (
            <button
              type="button"
              onClick={onClearAll}
              className="text-[11px] font-semibold text-zinc-500 hover:text-red-600 dark:text-zinc-400 dark:hover:text-red-400 transition"
            >
              Limpar dados
            </button>
          )}
        </div>

        {selectedKnowns.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto rounded-lg border border-paper-300 bg-paper-100 p-2 dark:border-study-line dark:bg-study-card">
            {selectedKnowns.map((id) => {
              const v = VARIABLE_BY_ID[id];
              if (!v) return null;
              return (
                <div
                  key={id}
                  className="flex items-center gap-1.5 rounded bg-indigo-50 px-2 py-1 text-xs border border-indigo-200 text-indigo-950 dark:bg-indigo-950/40 dark:border-indigo-800/60 dark:text-indigo-200 shadow-sm"
                >
                  <span className="font-mono font-bold text-indigo-700 dark:text-indigo-300 text-xs">
                    <InlineLatex>{v.symbol}</InlineLatex>
                  </span>
                  <span className="font-medium text-[11px] truncate max-w-[110px]">{v.name}</span>
                  <button
                    type="button"
                    onClick={() => onRemoveKnown(id)}
                    aria-label={`Remover ${v.name}`}
                    className="rounded p-0.5 text-indigo-400 hover:bg-indigo-200 hover:text-indigo-800 dark:hover:bg-indigo-900 dark:hover:text-indigo-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="rounded-lg border border-dashed border-paper-300 bg-paper-100 p-2.5 text-center text-xs text-zinc-500 dark:border-study-line dark:bg-study-card dark:text-zinc-400">
            Nenhum dado informado. Clique em <strong>+ Dado</strong> nas grandezas abaixo.
          </p>
        )}
      </div>

      {/* Step 2: Target Variable Chip */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
              2
            </span>
            <span className="font-serif text-xs font-bold text-zinc-900 dark:text-zinc-100">
              Incógnita Alvo a Descobrir
            </span>
          </div>
          {selectedTarget && (
            <button
              type="button"
              onClick={onClearTarget}
              className="text-[11px] font-semibold text-zinc-500 hover:text-amber-600 dark:text-zinc-400 dark:hover:text-amber-400 transition"
            >
              Trocar alvo
            </button>
          )}
        </div>

        {selectedTarget ? (
          <div className="flex items-center justify-between rounded-lg border border-amber-400 bg-amber-50/80 px-3 py-1.5 text-xs font-medium text-amber-950 dark:border-amber-700/80 dark:bg-amber-950/30 dark:text-amber-200 shadow-sm">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="font-mono font-bold text-sm">
                <InlineLatex>{VARIABLE_BY_ID[selectedTarget]?.symbol || selectedTarget}</InlineLatex>
              </span>
              <span className="font-bold">{VARIABLE_BY_ID[selectedTarget]?.name}</span>
              <span className="text-[11px] text-zinc-500">({VARIABLE_BY_ID[selectedTarget]?.unit})</span>
            </div>
            <button
              type="button"
              onClick={onClearTarget}
              className="rounded p-0.5 text-amber-600 hover:bg-amber-200 dark:text-amber-400 dark:hover:bg-amber-900"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <p className="rounded-lg border border-dashed border-paper-300 bg-paper-100 p-2 text-center text-xs text-zinc-500 dark:border-study-line dark:bg-study-card dark:text-zinc-400">
            Nenhum alvo definido. Clique em <strong>🎯 Alvo</strong> na grandeza que o exercício pede.
          </p>
        )}
      </div>
    </div>
  );
};
